/**
 * LWM1-479 (IP-175; design 2026-10-13 sections 1.4 and 1.5): which bank
 * statements the Bank Statement tab's lines come from, and whether
 * reconciliation is still processing, waiting for statements or complete -
 * the status panel's logic without React. Every fact is the server's (GET
 * cash/statement-status): the expected days of each collection account over
 * the last 10 business days, each day's status, reconciledThrough and the
 * overall state. The page only puts them in words.
 *
 * It never lets an incomplete position read as the full one: anything but
 * COMPLETED (or NO_ACCOUNTS, where nothing is expected) is incomplete, and
 * the banner then names the date the lines and matches reach, the
 * account-days not yet received and the ones that failed. A statement still
 * processing is named with its file and its stage, since the lines may change
 * when it finishes.
 */
import { isDecimal } from '@lfoc/domain/decimal.js';

import { dayShort } from '../../../../businessDay/businessDay.js';
import { formatInstant, formatMoney } from '../../../../format.js';

/** The screen's state (design 1.3): the first that holds, in this order. */
export const STATUS_STATE = Object.freeze({
  PROCESSING: 'PROCESSING',
  AWAITING_STATEMENTS: 'AWAITING_STATEMENTS',
  COMPLETED: 'COMPLETED',
  NO_ACCOUNTS: 'NO_ACCOUNTS',
});

/** How far back the server looks (cash/statement-status.js STATEMENT_HISTORY_BUSINESS_DAYS). */
export const HISTORY_BUSINESS_DAYS = 10;

/** One day of one account (design 1.2), in words and an antd tag colour. */
export const DAY_STATUS = Object.freeze({
  LOADED: { label: 'Loaded', color: 'green' },
  PROCESSING: { label: 'Processing', color: 'processing' },
  FAILED: { label: 'Failed', color: 'red' },
  MISSING: { label: 'Missing', color: 'warning' },
  NOT_DUE: { label: 'Not due yet', color: 'default' },
});

/** A processing file's stage (design 1.2): RECEIVED or SCANNING, VALIDATED within its 48 h, ACCEPTED. */
export const STAGE_LABEL = Object.freeze({
  SCANNING: 'being scanned',
  AWAITING_ACCEPTANCE: 'waiting for acceptance',
  IMPORTING: 'importing',
});

/** Why a day failed: its import's state when it has one (CHAIN_BLOCKED, REJECTED), else its batch's. */
const FAILURE_LABEL = Object.freeze({
  QUARANTINED: 'Quarantined',
  REJECTED_MALWARE: 'Malware found',
  SCAN_FAILED: 'Scan failed',
  REJECTED: 'Rejected',
  ACCEPTANCE_EXPIRED: 'Not accepted in time',
  VALIDATED: 'Not accepted in time',
  CHAIN_BLOCKED: 'Balance does not follow on from the previous statement',
});

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** '2026-10-12' -> 'Mon 12 Oct': a calendar date, its weekday the date's own; anything else as it is. */
export function dayLabel(date) {
  const m = DATE.exec(String(date ?? ''));
  if (!m) return String(date ?? '');
  return WEEKDAYS[new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))).getUTCDay()] + ' ' + dayShort(date);
}

/** A collection account in a few words: its bank, the last four digits of its number, and its fund - 'NATIONS TRUST BANK …0400 (UTIF)'. */
export function accountLabel(account) {
  const number = String(account?.accountNumber ?? '');
  const bank = [account?.bankName, number ? '…' + number.slice(-4) : null].filter(Boolean).join(' ');
  if (!bank) return String(account?.fundId ?? account?.accountId ?? 'An account');
  return bank + (account.fundId ? ' (' + account.fundId + ')' : '');
}

const plural = (n, one, many = one + 's') => n + ' ' + (n === 1 ? one : many);
const count = (n) => Number(n) || 0;
const capital = (text) => (text ? text[0].toUpperCase() + text.slice(1) : text);
const sameDay = (a, b) => a.accountId === b.accountId && a.statementDate === b.statementDate;

/** Whether the lines shown rest on statements still incomplete: every state but COMPLETED and NO_ACCOUNTS. Unknown before an answer. */
export function isIncomplete(status) {
  return Boolean(status?.state) && status.state !== STATUS_STATE.COMPLETED && status.state !== STATUS_STATE.NO_ACCOUNTS;
}

/** The expected days that failed, each with its account's fund: a failed upload for a non-business day changes nothing. */
function failedDays(status) {
  return (status?.accounts ?? []).flatMap((account) => (account.days ?? [])
    .filter((day) => day.status === 'FAILED' && day.expected !== false)
    .map((day) => ({ accountId: account.accountId, fundId: account.fundId, statementDate: day.statementDate, failure: day.failure ?? null })));
}

/** The account-days not yet received: the server's list (else its MISSING days), never one already named as failed. */
function missingDays(status, failed) {
  const listed = Array.isArray(status?.missing)
    ? status.missing
    : (status?.accounts ?? []).flatMap((account) => (account.days ?? [])
      .filter((day) => day.status === 'MISSING')
      .map((day) => ({ accountId: account.accountId, fundId: account.fundId, statementDate: day.statementDate })));
  return listed.filter((day) => !failed.some((f) => sameDay(f, day)));
}

/** What a failure says: the server's reason, else its import's or batch's state in words. */
function failureText(failure) {
  if (!failure) return null;
  return failure.reason || FAILURE_LABEL[failure.importState] || FAILURE_LABEL[failure.batchState] || failure.importState || failure.batchState || null;
}

/**
 * Days grouped by account, oldest first within each - 'NATIONS TRUST BANK
 * …0400 (UTIF): Mon 12 Oct, Tue 13 Oct; …' - each date with its note, if any.
 */
function byAccount(days, nameOf, noteOf = () => null) {
  const groups = new Map();
  for (const day of [...days].sort((a, b) => String(a.statementDate).localeCompare(String(b.statementDate)))) {
    const name = nameOf(day);
    const note = noteOf(day);
    const text = dayLabel(day.statementDate) + (note ? ' (' + note + ')' : '');
    if (!groups.has(name)) groups.set(name, []);
    if (!groups.get(name).includes(text)) groups.get(name).push(text);
  }
  return [...groups].map(([name, dates]) => name + ': ' + dates.join(', ')).join('; ');
}

/**
 * How far the lines and matches reach: the server's reconciledThrough (the
 * last day before the first gap), else which accounts have nothing loaded at
 * all (no latestStatementDate) - or, when every account has something, that
 * the window's earliest statements are the ones missing.
 */
function coverageLine(status, nameOf) {
  if (status.reconciledThrough) return 'Lines and matches below cover statements up to ' + dayLabel(status.reconciledThrough) + ' only.';
  const accounts = status.accounts ?? [];
  const none = accounts.filter((a) => !a.latestStatementDate);
  const window = 'No statement has been loaded in the last ' + HISTORY_BUSINESS_DAYS + ' business days';
  if (none.length && none.length === accounts.length) return window + '.';
  if (none.length) return window + ' for ' + none.map((a) => nameOf(a)).join(', ') + '; lines and matches below are incomplete.';
  return 'Statements from the start of the last ' + HISTORY_BUSINESS_DAYS + ' business days are missing; lines and matches below are incomplete.';
}

/** The account-days not yet received, and those that failed, as the banner says them (none: no line). */
function gapLines(status, nameOf) {
  const failed = failedDays(status);
  const missing = missingDays(status, failed);
  const lines = [];
  if (missing.length) lines.push('Not yet received: ' + byAccount(missing, nameOf) + '.');
  if (failed.length) lines.push('Failed: ' + byAccount(failed, nameOf, (day) => failureText(day.failure)) + '.');
  return lines;
}

/**
 * The panel's banner (design 1.3, 1.5): an antd Alert type, its title and
 * its lines, and whether the position shown is incomplete. Null before the
 * status arrives.
 *
 *   PROCESSING            info: each file still processing with its account,
 *                         date and stage; the lines may change when they finish
 *   AWAITING_STATEMENTS   warning: the date the lines and matches reach, the
 *                         account-days not yet received, the failed ones
 *   COMPLETED             success: complete through expectedThrough, and the
 *                         lines that still need attention
 *   NO_ACCOUNTS           info: nothing to reconcile
 *
 * @param {object|null} status GET cash/statement-status's answer
 * @param {{ accountLabel?: (account: object) => string }} [options]
 * @returns {{ type: 'info'|'warning'|'success', title: string, lines: string[], incomplete: boolean }|null}
 */
export function statusBanner(status, { accountLabel: labelOf = accountLabel } = {}) {
  if (!status?.state) return null;
  const accounts = new Map((status.accounts ?? []).map((a) => [a.accountId, a]));
  const nameOf = (row) => (accounts.has(row.accountId) ? labelOf(accounts.get(row.accountId)) : labelOf(row));
  const incomplete = isIncomplete(status);
  switch (status.state) {
    case STATUS_STATE.PROCESSING: {
      const files = (status.processing ?? []).map((p) => (p.fileName ?? 'A statement') + ' for ' + nameOf(p) + ' ' + dayLabel(p.statementDate)
        + ': ' + (STAGE_LABEL[p.stage] ?? String(p.stage ?? 'processing').toLowerCase()));
      const gaps = gapLines(status, nameOf);
      // Lines already short of the expected days say so too: a file finishing does not make them complete.
      const short = gaps.length || (status.expectedThrough && status.reconciledThrough !== status.expectedThrough);
      return {
        type: 'info',
        title: 'Statements still processing',
        lines: [...files, 'The lines and matches below may change when ' + (files.length === 1 ? 'it finishes.' : 'they finish.'),
          ...(short ? [coverageLine(status, nameOf)] : []), ...gaps],
        incomplete,
      };
    }
    case STATUS_STATE.AWAITING_STATEMENTS:
      return {
        type: 'warning',
        title: 'Reconciliation is based on incomplete statements',
        lines: [coverageLine(status, nameOf), ...gapLines(status, nameOf)],
        incomplete,
      };
    case STATUS_STATE.COMPLETED: {
      const outstanding = count(status.outstandingLines);
      return {
        type: 'success',
        title: 'Statements complete through ' + dayLabel(status.expectedThrough),
        lines: [outstanding > 0 ? plural(outstanding, 'line') + ' still ' + (outstanding === 1 ? 'needs' : 'need') + ' attention.' : 'No line needs attention.'],
        incomplete,
      };
    }
    case STATUS_STATE.NO_ACCOUNTS:
      return { type: 'info', title: 'No collection account to reconcile', lines: [], incomplete };
    default:
      return { type: 'info', title: 'Statement status: ' + status.state, lines: [], incomplete };
  }
}

/** A day's tag tooltip: a processing file's stage, why a day failed, what a missing or not-due day waits for. */
function dayTitle(day) {
  const notes = [];
  if (day.status === 'PROCESSING') notes.push(capital(STAGE_LABEL[day.batch?.stage] ?? 'processing') + '.');
  if (day.status === 'FAILED') {
    const why = failureText(day.failure);
    // A file turned away (quarantined, rejected, not accepted in time) is uploaded again; an import's own refusal is the server's to explain.
    notes.push((why ? why + '.' : 'The statement failed.') + (day.failure?.importState ? '' : ' Upload the day\'s statement again.'));
  }
  if (day.status === 'MISSING') notes.push('Not received: the bank\'s statement for a day is due on the next business day.');
  if (day.status === 'NOT_DUE') notes.push('The bank\'s statement for today is due on the next business day.');
  if (day.expected === false) notes.push('Not a business day: a statement was uploaded for it all the same.');
  return notes.join(' ') || undefined;
}

/** Uploaded (file, by, when): the import's file and who accepted it, else the processing or failed file's. */
function uploadedText(day) {
  const parts = day.import
    ? [day.import.fileName, day.import.acceptedByName, day.import.acceptedAt && formatInstant(day.import.acceptedAt)]
    : day.batch
      ? [day.batch.fileName, day.batch.uploadedByName, day.batch.uploadedAt && formatInstant(day.batch.uploadedAt)]
      : [day.failure?.fileName];
  return parts.filter(Boolean).join(' · ') || null;
}

const moneyOf = (value) => (isDecimal(value) ? formatMoney(value) : null);

/**
 * The Statement history table (design 1.5): one row per account-day, newest
 * date first, then by account. A loaded day carries its import's figures;
 * any other day only its status (and a processing or failed day its file).
 *
 * @returns {{ key: string, statementDate: string, dateLabel: string, expected: boolean, accountId: string, fundId: string,
 *             accountLabel: string, status: string, statusLabel: string, statusColor: string, statusTitle?: string,
 *             lines: string|null, credits: string|null, debits: string|null, reconciled: string|null, outstanding: number|null,
 *             uploaded: string|null }[]}
 */
export function historyRows(status) {
  const rows = [];
  for (const account of status?.accounts ?? []) {
    const name = accountLabel(account);
    (account.days ?? []).forEach((day, i) => {
      const tag = DAY_STATUS[day.status] ?? { label: String(day.status ?? '—'), color: 'default' };
      const loaded = day.import ?? null;
      rows.push({
        key: account.accountId + '|' + day.statementDate + '|' + i,
        statementDate: day.statementDate,
        dateLabel: dayLabel(day.statementDate),
        expected: day.expected !== false,
        accountId: account.accountId,
        fundId: account.fundId,
        accountLabel: name,
        status: day.status,
        statusLabel: tag.label,
        statusColor: tag.color,
        statusTitle: dayTitle(day),
        lines: loaded ? count(loaded.lineCount) + ' (' + count(loaded.duplicateLineCount) + ' dup)' : null,
        credits: loaded ? moneyOf(loaded.credits) : null,
        debits: loaded ? moneyOf(loaded.debits) : null,
        reconciled: loaded ? count(loaded.matchedLines) + ' / ' + count(loaded.creditLines) : null,
        outstanding: loaded ? count(loaded.outstandingLines) : null,
        uploaded: uploadedText(day),
      });
    });
  }
  return rows.sort((a, b) => String(b.statementDate).localeCompare(String(a.statementDate)) || a.accountLabel.localeCompare(b.accountLabel));
}
